import './SaveChartAsNewDialog.scss';
import { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { Button } from '@Components/Button/Button';
import { Appearance, Design } from '@Components/designConstants';
import { Radio } from '@Components/FormCheck';

// Where a newly-minted chart should land relative to the card being edited. Spelled out as two radios
// (not a checkbox) so both outcomes read in plain language — clarity was the explicit design ask.
export const SaveAsNewPlacement = {
  // Repoint THIS card at the new chart; the shared original and every other dashboard stay untouched.
  HERE: 'here',
  // Mint the chart to the library only; this dashboard keeps pointing at the original.
  LIBRARY: 'library'
} as const;
export type SaveAsNewPlacementType = (typeof SaveAsNewPlacement)[keyof typeof SaveAsNewPlacement];

interface SaveChartAsNewDialogProps {
  show: boolean;
  // Seeds the name field — the working copy's title, so the user usually just confirms.
  defaultName: string;
  loading?: boolean;
  onCancel: () => void;
  onSave: (name: string, placement: SaveAsNewPlacementType) => void;
  // Whether "Show it here" is on offer. False when the caller may not change the DASHBOARD:
  // repointing a card rewrites the layout and saves it, which is a dashboard write, not a chart
  // write. Only the dashboard card's controls pass this — on the chart page "Show it here" merely
  // navigates, so it stays available there.
  allowRepoint?: boolean;
}

// The naming + placement dialog for a dashboard card's "Save as a new chart" exit. Beyond the name it
// forces the user to pick what happens to THIS card: show the new chart here (repoint) or just file it
// to the library and leave the dashboard on the original.
export const SaveChartAsNewDialog = ({
  show,
  defaultName,
  loading = false,
  onCancel,
  onSave,
  allowRepoint = true
}: SaveChartAsNewDialogProps) => {
  const [name, setName] = useState(defaultName);
  // `allowRepoint` MUST decide this value, not just hide the radios below. Hiding a control does
  // not change the state behind it, so a dialog that only hid them would still submit HERE and the
  // card would be repointed anyway — the exact write the prop exists to prevent.
  const [placement, setPlacement] = useState<SaveAsNewPlacementType>(
    allowRepoint ? SaveAsNewPlacement.HERE : SaveAsNewPlacement.LIBRARY
  );

  // Re-seed the name each time the dialog opens (a fresh working copy title) and reset the placement to
  // the default. Keyed on `show` so reopening for a different card doesn't keep the last name.
  useEffect(() => {
    if (show) {
      setName(defaultName);
      // Same expression as the initial state — the reset runs on every open, so leaving HERE here
      // would quietly restore the hidden choice each time the dialog appeared.
      setPlacement(allowRepoint ? SaveAsNewPlacement.HERE : SaveAsNewPlacement.LIBRARY);
    }
  }, [show, defaultName, allowRepoint]);

  const trimmed = name.trim();

  return (
    <Modal
      show={show}
      onHide={onCancel}
      contentClassName='save-chart-as-new-dialog'
      centered
      backdrop
      keyboard
    >
      <Modal.Body>
        <h4 className='dialog-title'>Save as a new chart</h4>
        <Form.Group className='dialog-field'>
          <Form.Label>Chart name</Form.Label>
          <Form.Control
            autoFocus
            value={name}
            placeholder='Chart name'
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && trimmed) {
                onSave(trimmed, placement);
              }
            }}
          />
        </Form.Group>
        {allowRepoint && (
          <div className='dialog-field'>
            <Radio
              id='save-as-new-here'
              name='save-as-new-placement'
              checked={placement === SaveAsNewPlacement.HERE}
              onChange={() => setPlacement(SaveAsNewPlacement.HERE)}
              label={
                <span className='dialog-radio-label'>
                  <span className='dialog-radio-title'>Show it here</span>
                  <span className='dialog-radio-consequence'>
                    This card switches to the new chart.
                  </span>
                </span>
              }
            />
            <Radio
              id='save-as-new-library'
              name='save-as-new-placement'
              checked={placement === SaveAsNewPlacement.LIBRARY}
              onChange={() => setPlacement(SaveAsNewPlacement.LIBRARY)}
              label={
                <span className='dialog-radio-label'>
                  <span className='dialog-radio-title'>Just save to my charts</span>
                  <span className='dialog-radio-consequence'>
                    This dashboard keeps the original; find the new chart in your library.
                  </span>
                </span>
              }
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={loading}
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button loading={loading} disabled={!trimmed} onClick={() => onSave(trimmed, placement)}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
